import React, { useState, useEffect } from 'react';
import cn from 'clsx';
import { LoadingOutlined } from '@ant-design/icons';
import Notification from 'components/Notification';
import { ASpin } from 'components/Antd';
import { apiDownloadSharedDocuments } from 'api/document-management';
import { apiGetSharedDocumentInfo } from 'api/document-management';
import { ReactComponent as FolderSvg } from 'assets/icons/New/folder-icon.svg';
import { ReactComponent as CloseSvg } from 'assets/icons/New/close-icon.svg';
import { ReactComponent as QMASLogoSvg } from 'assets/icons/New/qmas-logo.svg';
import { getDateWithFormat } from 'util/date';
import useDownloadFile from 'hooks/useDownloadFile';
import { formatBytes } from 'util/file';
import { ACheckbox } from 'components/Antd';
import classes from './DocumentShare.module.scss';

const LoadingComponent = ({ isFull }) => {
  return (
    <div
      className={cn(classes.loadingWrapper, {
        [classes.loadingFull]: isFull,
      })}>
      <ASpin indicator={<LoadingOutlined spin />} />{' '}
    </div>
  );
};

const DocumentShare = ({ match }) => {
  const [download] = useDownloadFile();
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadAllLoading, setDownloadAllLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [documentInfo, setDocumentInfo] = useState({});
  const [notification, setNotification] = React.useState({ type: null, message: null });
  const documents = documentInfo?.documentLists || [];

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const res = await apiGetSharedDocumentInfo(match.params?.token);
      if ([200, 201].includes(res?.data?.code)) {
        setDocumentInfo(res?.data?.data);
      } else {
        setDocumentInfo({
          error: true,
        });
        // setNotification({ type: 'error', message: 'Failed to load documents' });
      }
      setLoading(false);
    } catch (error) {
      setDocumentInfo({
        error: true,
      });
      // setNotification({ type: 'error', message: 'Failed to load documents' });
      setLoading(false);
    }
  };
  const handleDownloadDocuments = async (ids, cb) => {
    try {
      const res = await apiDownloadSharedDocuments({
        documentListIds: ids,
      });
      if (res?.data) {
        const fileName = res?.headers['content-disposition']?.split('filename=')[1];
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
        download(fileName, url);
        if (cb) cb();
        setNotification({
          type: 'success',
          message: 'Download Succeeded',
        });
      } else {
        if (cb) cb();
        setNotification({
          type: 'error',
          message: 'Download Failed',
        });
      }
    } catch (error) {
      if (cb) cb();
      setNotification({
        type: 'error',
        message: 'Download Failed',
      });
    }
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  return (
    <div className={classes.wrapper}>
      <div className={classes.inner}>
        <QMASLogoSvg />
        {loading ? (
          <LoadingComponent isFull />
        ) : (
          <>
            {documentInfo?.error ? (
              <span className={classes.error}>
                Invalid token. Please check the link or contact the sender.
              </span>
            ) : (
              <>
                <h2>{documentInfo?.company} shared folder to you</h2>
                <div className={classes.tableWrapper}>
                  <div className={classes.topInfo}>
                    <span>
                      Expire at: {getDateWithFormat(documentInfo?.expiryDate, 'DD/MM/YYYY')}{' '}
                    </span>
                    <span
                      onClick={() => {
                        if (!downloadAllLoading) {
                          setDownloadAllLoading(true);
                          handleDownloadDocuments(
                            documents.map((obj) => obj.id),
                            () => {
                              setDownloadAllLoading(false);
                            }
                          );
                        }
                      }}>
                      {downloadAllLoading && <LoadingComponent />}
                      <span>Download All</span>
                    </span>
                  </div>
                  <div className={classes.table}>
                    <div className={classes.tableHeader}>
                      {selectedIds.length > 0 && (
                        <>
                          <span>{selectedIds.length} Selected</span>
                          <span className={classes.border}></span>
                          <span
                            onClick={() => {
                              if (!downloadLoading) {
                                setDownloadLoading(true);
                                handleDownloadDocuments(selectedIds, () => {
                                  setDownloadLoading(false);
                                });
                              }
                            }}>
                            {downloadLoading && <LoadingComponent />}
                            <span>Download</span>
                          </span>
                          <span
                            onClick={() => {
                              setSelectedIds([]);
                            }}>
                            <CloseSvg />
                          </span>
                        </>
                      )}
                    </div>
                    <ul className={classes.tableList}>
                      {documents.map((obj) => {
                        const tId = obj.id;
                        return (
                          <li
                            key={tId}
                            className={cn({
                              [classes.selected]: selectedIds.includes(tId),
                            })}>
                            <span>
                              <ACheckbox
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedIds([...selectedIds, tId]);
                                  } else {
                                    setSelectedIds(selectedIds.filter((id) => id !== tId));
                                  }
                                }}
                                checked={selectedIds.includes(tId)}
                              />
                            </span>
                            <span>
                              <FolderSvg />
                              <span>{obj.fileName}</span>
                            </span>
                            <span>{formatBytes(obj.size)}</span>
                            <span>{getDateWithFormat(obj.updatedAt, 'DD-MM-YYYY')}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
      <Notification
        notification={notification}
        onClose={() => setNotification({ type: null, message: null })}
        handleOnClose={() => setNotification({ type: null, message: null })}
      />
    </div>
  );
};

export default DocumentShare;
